import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dialogsAPI } from '../../services/api';

const HistorySidebar = ({ coachId }) => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDialogs = async () => {
      setIsLoading(true);
      try {
        const response = await dialogsAPI.getAll({ 
          has_messages: true, 
          coach_id: coachId 
        });
        
        // The API returns dialogs inside an 'items' array
        const dialogsArray = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.items || response.data?.data || []);
        
        // Normalize the data format from the API
        const formattedSessions = dialogsArray.map(dialog => {
          const createdAt = new Date(dialog.created_at || dialog.updated_at);
          const source = (dialog.metadata?.source || 'chat').toLowerCase();
          
          let type = 'Chat';
          let icon = 'forum';
          let colorClass = 'bg-emerald-500/10 text-emerald-400';
          
          if (source.includes('voice')) {
            type = 'Voice';
            icon = 'mic';
            colorClass = 'bg-[var(--tangerine)]/10 text-[var(--tangerine)]';
          } else if (source.includes('video')) {
            type = 'Video';
            icon = 'videocam';
            colorClass = 'bg-blue-500/10 text-blue-400';
          }
          
          return {
            id: dialog.id,
            date: createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            time: createdAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
            duration: 'Live',
            topic: dialog.metadata?.topic || `${type} Session`,
            type: type,
            icon: icon,
            colorClass: colorClass
          };
        });
        
        setSessions(formattedSessions);
      } catch (error) {
        console.error('Failed to fetch dialogs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDialogs();
  }, [coachId]);

  return (
    <aside className="hidden xl:flex w-72 flex-col shrink-0 space-y-6">
      <div className="bg-[var(--surface-white)] rounded-[2.5rem] shadow-xl shadow-black/5 border border-[var(--border-light)] overflow-hidden p-6 flex-1 flex flex-col">
        <section className="space-y-6 flex-1 flex flex-col">
          <header className="px-1">
            <h3 className="text-xs font-black text-[var(--text-secondary)]/60 uppercase tracking-widest flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-base">history</span>
              Conversation History
            </h3>
            <p className="text-[10px] text-[var(--text-secondary)]/40 font-bold uppercase tracking-wider">Your previous sessions</p>
          </header>

          <div className="space-y-3 overflow-y-auto pr-2 max-h-[calc(100vh-300px)] custom-scrollbar">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 opacity-40">
                <div className="w-8 h-8 border-2 border-[var(--tangerine)] border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-[10px] font-black uppercase tracking-widest">Loading history...</p>
              </div>
            ) : sessions.length > 0 ? (
              sessions.map((session) => (
                <div 
                  key={session.id} 
                  onClick={() => navigate(`/user/history/${session.id}`)}
                  className="group p-4 rounded-3xl bg-[var(--off-white)] border border-[var(--border-light)] hover:border-[var(--tangerine)]/30 hover:shadow-md transition-all cursor-pointer relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                     <span className="material-symbols-outlined text-xs text-[var(--tangerine)]">play_circle</span>
                  </div>
                  
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${session.colorClass}`}>
                      <span className="material-symbols-outlined text-sm">
                        {session.icon}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] font-black text-[var(--text-primary)] truncate uppercase tracking-tight">{session.topic}</p>
                      <p className="text-[9px] font-bold text-[var(--text-secondary)]/60">{session.date} • {session.time}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 opacity-40">
                <p className="text-[10px] font-black uppercase tracking-widest">No previous sessions found</p>
              </div>
            )}
          </div>

          <button className="mt-auto w-full py-4 rounded-2xl border border-dashed border-[var(--border-light)] text-[var(--text-secondary)] hover:text-[var(--tangerine)] hover:border-[var(--tangerine)]/30 text-[10px] font-black uppercase tracking-widest transition-all">
            See All Recordings
          </button>
        </section>
      </div>
    </aside>
  );
};

export default HistorySidebar;
